import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ReferralReceiptBadge } from '@/components/patient/ReferralReceiptBadge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAuth } from '@/lib/auth';
import { canImportReceiptManually } from '@/lib/referral/manualReceiptImport';
import { formatReferralDateTime } from '@/lib/referral/referralDateTime';
import { ApiCallError, callFunction } from '@/lib/api';
import {
  REFERRAL_OUTBOX_KINDS,
  useReferralOutbox,
  type OutboundReferralKind,
  type OutboxData,
  type OutboxRow,
} from '@/components/patient/referralOutboxData';
import { OutboxVansPanel, vansAttemptsQueryKey } from '@/components/patient/OutboxVansPanel';
import { downloadLatin1Edifact, downloadLatin1Xml } from '@/lib/referral/latin1Xml';
import { downloadUtf8Xml } from '@/lib/referral/vansEnvelope';
import {
  isOutboundReferralStatus,
  knownRefuseCode,
  type ReceiptHistoryItem,
  type RefuseCode,
} from '@/lib/referral/referralReceipt';
import {
  receiptImportErrorFeedback,
  receiptImportFeedback,
  type ReceiptImportFeedback,
  type ReceiptImportResult,
} from '@/lib/referral/receiptImportFeedback';
import { applyImportedReceipt, type ImportedReceipt } from '@/lib/referral/importedReceipt';
import { FORM_ERROR_TYPE, FORM_LABEL_TYPE, TYPE, UI_SECTION } from '@/lib/typography';
import { cn } from '@/utils/cn';

type ImportResult = ReceiptImportResult & {
  /** Communicate's id of the imported receipt. */
  id?: string;
  refuseCode?: string | null;
  companionExchangeId?: string;
  /** vans-rejected: why the VANSEnvelope could not be unpacked (C21). */
  errorText?: string;
};

type PayloadResult = {
  xml?: string;
  edi?: string;
};

export function HospitalReferralOutbox({
  clinicId,
  patientId,
  revision,
  kinds = REFERRAL_OUTBOX_KINDS,
  title,
  emptyText,
  rowExtra,
}: {
  clinicId: string;
  patientId: string;
  revision: number;
  /** Kinds this outbox lists; the GGOP form passes ['GGOP']. Default: the referral and DIS kinds. */
  kinds?: readonly OutboundReferralKind[];
  title?: string;
  emptyText?: string;
  /** Extra content per row (the GGOP outbox: show and print the plan). */
  rowExtra?: (row: OutboxRow) => ReactNode;
}) {
  const { t } = useTranslation();
  const { isPlatformAdmin, isClinicAdmin } = useAuth();
  const canImport = canImportReceiptManually({
    isPlatformAdmin,
    isClinicAdmin: isClinicAdmin(clinicId),
  });
  const queryClient = useQueryClient();
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const query = useReferralOutbox(clinicId, patientId, revision);

  async function refresh() {
    await queryClient.invalidateQueries({
      queryKey: ['hospital-referral-outbox', clinicId, patientId],
    });
  }

  /**
   * Medcom (GGOP): show the imported receipt on its row at once, without a reload. The
   * receipt is put on the cached row, the outbox is refetched, and the receipt is put back
   * if the refetched data does not hold it yet (a no-op once it does).
   */
  async function showImportedReceipt(rowId: string, receipt: ImportedReceipt) {
    const outboxKey = ['hospital-referral-outbox', clinicId, patientId];
    const apply = () =>
      queryClient.setQueriesData<OutboxData>({ queryKey: outboxKey }, (data) =>
        applyImportedReceipt(data, rowId, receipt),
      );
    apply();
    await refresh();
    apply();
  }

  async function onDownload(row: OutboxRow, exchangeId: string, fileName: string) {
    setBusyId(row.id);
    setRowError((current) => ({ ...current, [row.id]: '' }));
    try {
      const result = await callFunction<
        { action: 'payload'; clinicId: string; exchangeId: string },
        PayloadResult
      >('communicate-ref', {
        action: 'payload',
        clinicId,
        exchangeId,
      });
      if (result.xml === undefined) throw new Error('Missing XML payload');
      // A GGOP (GGOP-S 3.2.1) and XDIS18/XDIS21 (NSK 1.0.3) are UTF-8; the referral and DIS letters are Latin-1.
      if (row.kind === 'GGOP' || row.kind === 'XDIS18' || row.kind === 'XDIS21') downloadUtf8Xml(result.xml, fileName);
      else downloadLatin1Xml(result.xml, fileName);
    } catch (err) {
      setRowError((current) => ({
        ...current,
        [row.id]:
          err instanceof ApiCallError
            ? err.message
            : t('patients.hospital_referral.outbox_download_failed', 'XML-filen kunne ikke hentes.'),
      }));
    } finally {
      setBusyId(null);
    }
  }

  async function onDownloadEdifact(row: OutboxRow) {
    setBusyId(row.id);
    setRowError((current) => ({ ...current, [row.id]: '' }));
    try {
      const result = await callFunction<
        { action: 'payload'; clinicId: string; exchangeId: string; format: 'EDIFACT' },
        PayloadResult
      >('communicate-ref', {
        action: 'payload',
        clinicId,
        exchangeId: row.exchange_id,
        format: 'EDIFACT',
      });
      if (result.edi === undefined) throw new Error('Missing EDIFACT payload');
      downloadLatin1Edifact(result.edi, `REF06_${row.envelope_identifier}.edi`);
    } catch (err) {
      setRowError((current) => ({
        ...current,
        [row.id]:
          err instanceof ApiCallError
            ? err.message
            : t('patients.hospital_referral.outbox_download_edifact_failed', 'EDIFACT-filen kunne ikke hentes.'),
      }));
    } finally {
      setBusyId(null);
    }
  }

  function report(row: OutboxRow, feedback: ReceiptImportFeedback) {
    if (feedback.tone === 'error') {
      toast.error(feedback.message, { duration: 10_000 });
      setRowError((current) => ({ ...current, [row.id]: feedback.message }));
    } else {
      toast.success(feedback.message, { duration: 6_000 });
    }
  }

  async function onImport(row: OutboxRow, file: File) {
    setBusyId(row.id);
    setRowError((current) => ({ ...current, [row.id]: '' }));
    try {
      const xml = new TextDecoder('iso-8859-1').decode(new Uint8Array(await file.arrayBuffer()));
      const result = await callFunction<
        { action: 'import'; clinicId: string; xml: string; referralId: string },
        ImportResult
      >('communicate-ctl', {
        action: 'import',
        clinicId,
        xml,
        referralId: row.id,
      });
      // C17: a VANS Receipt is coupled to its envelope in Communicate, not to the
      // XCTL status or the XCTL receipt history.
      if (result.importKind === 'vans-receipt') {
        await queryClient.invalidateQueries({ queryKey: vansAttemptsQueryKey(clinicId, row.id) });
        toast.success(
          t('patients.hospital_referral.receipt_import_vans', 'VANS-kvitteringen er indlæst på kuverten.'),
          { duration: 6_000 },
        );
        return;
      }
      if (result.importKind === 'vans-rejected') {
        report(
          row,
          receiptImportErrorFeedback(
            {
              message:
                result.errorText ||
                t('patients.hospital_referral.outbox_unreadable', 'Filen kunne ikke læses.'),
            },
            t,
          ),
        );
        return;
      }
      const companionFile = result.companionExchangeId
        ? row.companions.find((c) => c.exchangeId === result.companionExchangeId)?.fileName ?? null
        : null;
      const feedback = receiptImportFeedback(result, row, t, companionFile);
      report(row, feedback);
      if (
        feedback.refresh &&
        result.id &&
        result.status &&
        result.status !== 'built' &&
        (result.attachment === 'matched' || result.attachment === 'doublet')
      ) {
        await showImportedReceipt(row.id, {
          id: result.id,
          status: result.status,
          refuseCode: result.refuseCode ?? null,
          refuseText: result.refuseText ?? null,
          receivedAt: new Date().toISOString(),
        });
      } else if (feedback.refresh) await refresh();
    } catch (err) {
      report(row, receiptImportErrorFeedback(err, t));
    } finally {
      setBusyId(null);
    }
  }

  const rows = (query.data?.rows ?? []).filter((row) => kinds.includes(row.kind));

  return (
    <Section title={title ?? t('patients.hospital_referral.outbox_section', 'Udbakke')}>
      {query.isLoading && !query.data && (
        <div className="flex flex-col gap-s3" aria-busy>
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}
      {query.isError && (
        <p role="alert" className={FORM_ERROR_TYPE}>
          {t('patients.hospital_referral.outbox_list_failed', 'Udbakken kunne ikke hentes.')}
        </p>
      )}
      {query.data?.syncFailed && (
        <p role="alert" className={FORM_ERROR_TYPE}>
          {t(
            'patients.hospital_referral.outbox_sync_failed',
            'Kvitteringerne kunne ikke kontrolleres. Status vises, som den er gemt.',
          )}
        </p>
      )}
      {!(query.isLoading && !query.data) && !query.isError && rows.length === 0 && (
        <p className={TYPE.body}>
          {emptyText ?? t('patients.hospital_referral.outbox_empty', 'Ingen henvisninger i udbakken.')}
        </p>
      )}
      <div className="space-y-4">
        {rows.map((row) => {
          const status = isOutboundReferralStatus(row.status) ? row.status : null;
          const refuse = knownRefuseCode(row.refuse_code);
          const refuseText = query.data?.refuseTextById[row.id];
          const sentAt = formatReferralDateTime(row.created_at);
          const receiptAt = formatReferralDateTime(query.data?.receiptReceivedAtById[row.id]);
          const negative = status === 'xctl01' || status === 'xctl02';
          return (
            <div
              key={row.id}
              className={cn(
                'rounded-md border p-3',
                negative
                  ? 'border-ds-red bg-ds-red-tint  '
                  : 'border-ds-line ',
              )}
            >
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <ReferralReceiptBadge status={row.status} />
                <span className={cn(TYPE.body, 'text-ds-muted ')}>
                  {kindLabel(row.kind, t)}
                  {row.copy_of_id && (
                    <span className="ml-1 rounded bg-ds-teal-tint px-1.5 py-0.5 text-xs font-medium text-ds-teal">
                      {t('patients.hospital_referral.outbox_copy', 'Kopi')}
                    </span>
                  )}
                  {sentAt && (
                    <>
                      {' · '}
                      {t('patients.hospital_referral.outbox_sent_at', 'Afsendt')} {sentAt}
                    </>
                  )}
                  {receiptAt && (
                    <>
                      {' · '}
                      {t('patients.hospital_referral.outbox_receipt_at', 'Kvittering modtaget')} {receiptAt}
                    </>
                  )}
                </span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <ReadOnly
                  label={t('patients.hospital_referral.receiver_section', 'Modtager')}
                  value={row.receiver_organisation_name || t('patients.hospital_referral.empty', 'Ikke udfyldt')}
                />
                <ReadOnly
                  label={t('patients.hospital_referral.ean', 'EAN')}
                  value={row.receiver_ean}
                />
                <ReadOnly
                  label={t('patients.hospital_referral.outbox_envelope', 'Kuvert-nummer')}
                  value={row.envelope_identifier}
                />
                <ReadOnly
                  label={t('patients.hospital_referral.outbox_letter', 'Brev-nummer')}
                  value={row.letter_identifier}
                />
                <ReadOnly
                  label={t('patients.hospital_referral.outbox_kind', 'Henvisningstype')}
                  value={kindLabel(row.kind, t)}
                />
                <ReadOnly
                  label={t('patients.hospital_referral.outbox_sent_at', 'Afsendt')}
                  value={sentAt ?? t('patients.hospital_referral.empty', 'Ikke udfyldt')}
                />
                <ReadOnly
                  label={t('patients.hospital_referral.outbox_receipt_at', 'Kvittering modtaget')}
                  value={receiptAt ?? t('patients.hospital_referral.outbox_receipt_at_none', 'Ingen kvittering endnu')}
                />
                {negative && refuse && (
                  <ReadOnly
                    label={t('patients.hospital_referral.outbox_refuse', 'Afvisningskode')}
                    value={refuseLabel(refuse, t)}
                  />
                )}
              </div>
              {negative && refuseText && (
                <p className={cn('mt-3 whitespace-pre-wrap', TYPE.body)}>{refuseText}</p>
              )}
              <ReceiptHistory
                items={query.data?.receiptHistoryById[row.id] ?? []}
                testId="outbox-receipt-history"
                t={t}
              />
              {row.companions.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="outbox-companion-receipts">
                  <div className={FORM_LABEL_TYPE}>
                    {t('patients.hospital_referral.outbox_companion_receipts', 'Kvittering pr. bilag')}
                  </div>
                  {row.companions.map((companion) => {
                    const receipt = query.data?.companionReceiptByExchangeId[companion.exchangeId];
                    const receiptTime = formatReferralDateTime(receipt?.receivedAt ?? undefined);
                    const companionNegative = receipt?.status === 'xctl01' || receipt?.status === 'xctl02';
                    const companionRefuse = knownRefuseCode(receipt?.refuseCode ?? null);
                    return (
                      <div
                        key={companion.exchangeId}
                        className={cn(
                          'rounded border px-2 py-1.5',
                          companionNegative
                            ? 'border-ds-red bg-ds-red-tint  '
                            : 'border-ds-line ',
                        )}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <ReferralReceiptBadge status={receipt?.status ?? 'built'} />
                          <span className={cn(TYPE.body, 'break-all text-ds-text ')}>
                            {companion.fileName}
                            {receiptTime && (
                              <>
                                {' · '}
                                {t('patients.hospital_referral.outbox_receipt_at', 'Kvittering modtaget')} {receiptTime}
                              </>
                            )}
                            {companionNegative && companionRefuse && <>{' · '}{refuseLabel(companionRefuse, t)}</>}
                          </span>
                        </div>
                        {companionNegative && receipt?.refuseText && (
                          <p className={cn('mt-1 whitespace-pre-wrap', TYPE.body)}>{receipt.refuseText}</p>
                        )}
                        {(receipt?.history?.length ?? 0) > 1 && (
                          <ReceiptHistory
                            items={receipt?.history ?? []}
                            testId="outbox-companion-receipt-history"
                            t={t}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {rowError[row.id] && (
                <p role="alert" className={cn('mt-3', FORM_ERROR_TYPE)}>
                  {rowError[row.id]}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busyId === row.id}
                  onClick={() => void onDownload(row, row.exchange_id, `${row.kind}_${row.envelope_identifier}.xml`)}
                >
                  {t('patients.hospital_referral.download', 'Hent XML')}
                </Button>
                {row.kind === 'XREF06' && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={busyId === row.id}
                    onClick={() => void onDownloadEdifact(row)}
                  >
                    {t('patients.hospital_referral.download_edifact', 'Hent EDIFACT (REF06)')}
                  </Button>
                )}
                {row.companions.map((companion) => (
                  <Button
                    key={companion.exchangeId}
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={busyId === row.id}
                    onClick={() =>
                      void onDownload(
                        row,
                        companion.exchangeId,
                        `${row.kind.startsWith('XREF') ? 'XBIN02' : 'XBIN01'}_${companion.fileName.replace(/[\\/]/g, '_')}.xml`,
                      )
                    }
                  >
                    {t('patients.hospital_referral.outbox_download_companion', 'Hent bilag')}: {companion.fileName}
                  </Button>
                ))}
                {canImport && (
                  <div className="min-w-0 flex-1">
                    <Input
                      type="file"
                      accept=".xml,application/xml,text/xml"
                      label={t('patients.hospital_referral.outbox_import_admin', 'Indlæs kvittering (kun admin)')}
                      disabled={busyId === row.id}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (file) void onImport(row, file);
                      }}
                    />
                  </div>
                )}
              </div>
              <OutboxVansPanel clinicId={clinicId} row={row} />
              {rowExtra?.(row)}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function kindLabel(
  kind: OutboundReferralKind,
  t: (key: string, fallback: string) => string,
): string {
  switch (kind) {
    case 'XREF01':
      return t('patients.hospital_referral.outbox_kind_xref01', 'Sygehushenvisning');
    case 'XREF02':
      return t('patients.hospital_referral.outbox_kind_xref02', 'Billeddiagnostisk henvisning');
    case 'XREF06':
      return t('patients.hospital_referral.outbox_kind_xref06', 'Speciallægehenvisning');
    case 'XDIS91':
      return t('patients.hospital_referral.outbox_kind_xdis91', 'Korrespondance (XDIS91)');
    case 'XDIS13':
      return t('patients.hospital_referral.outbox_kind_xdis13', 'Bookingsvar (XDIS13)');
    case 'XDIS01':
      return t('patients.hospital_referral.outbox_kind_xdis01', 'Udskrivningsepikrise (XDIS01)');
    case 'XDIS02':
      return t('patients.hospital_referral.outbox_kind_xdis02', 'Ambulant epikrise (XDIS02)');
    case 'XDIS03':
      return t('patients.hospital_referral.outbox_kind_xdis03', 'Skadestueepikrise (XDIS03)');
    case 'XDIS05':
      return t('patients.hospital_referral.outbox_kind_xdis05', 'Billeddiagnostisk epikrise (XDIS05)');
    case 'GGOP':
      return t('patients.ggop.outbox_kind', 'Genoptræningsplan (GGOP)');
    case 'XDIS18':
      return t('patients.xdis.outbox_kind', 'Udskrivningsrapport (XDIS18)');
    case 'XDIS21':
      return t('patients.xdis.outbox_kind21', 'Plejeforløbsplan (XDIS21)');
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
}

function refuseLabel(code: RefuseCode, t: (key: string, fallback: string) => string): string {
  switch (code) {
    case 'ukendt_lokationsnummer':
      return t('patients.hospital_referral.refuse_ukendt_lokationsnummer', 'Ukendt lokationsnummer');
    case 'problem_med_version':
      return t('patients.hospital_referral.refuse_problem_med_version', 'Problem med version');
    case 'problem_med_modtagerID':
      return t('patients.hospital_referral.refuse_problem_med_modtagerID', 'Problem med modtager-id');
    case 'syntaksfejl':
      return t('patients.hospital_referral.refuse_syntaksfejl', 'Syntaksfejl');
    case 'ikke_specificeret':
      return t('patients.hospital_referral.refuse_ikke_specificeret', 'Ikke specificeret');
    default: {
      const unexpected: never = code;
      return unexpected;
    }
  }
}

function receiptKindLabel(
  item: ReceiptHistoryItem,
  t: (key: string, fallback: string) => string,
): string {
  switch (item.kind) {
    case 'XCTL01':
      return t('patients.hospital_referral.receipt_history_xctl01', 'Negativ VANS-kvittering (XCTL01)');
    case 'XCTL02':
      return t('patients.hospital_referral.receipt_history_xctl02', 'Negativ kvittering (XCTL02)');
    case 'XCTL03':
      return t('patients.hospital_referral.receipt_history_xctl03', 'Positiv kvittering (XCTL03)');
    default: {
      const unexpected: never = item.kind;
      return unexpected;
    }
  }
}

/**
 * Every receipt loaded on a sent referral (or attachment), newest first. The
 * newest sets the status ("Gældende"); earlier ones stay listed (REF02-S 2.1–2.3).
 */
function ReceiptHistory({
  items,
  testId,
  t,
}: {
  items: readonly ReceiptHistoryItem[];
  testId: string;
  t: (key: string, fallback: string) => string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mt-3" data-testid={testId}>
      <div className={FORM_LABEL_TYPE}>
        {t('patients.hospital_referral.receipt_history', 'Kvitteringshistorik')}
      </div>
      <ol className="mt-1 space-y-1">
        {items.map((item, index) => {
          const negative = item.status === 'xctl01' || item.status === 'xctl02';
          const refuse = knownRefuseCode(item.refuseCode);
          const at = formatReferralDateTime(item.receivedAt);
          return (
            <li
              key={item.id ?? `${item.kind}-${item.receivedAt ?? ''}-${index}`}
              data-receipt-kind={item.kind}
              data-receipt-current={item.current ? 'true' : 'false'}
              className={cn(
                'rounded border px-2 py-1',
                TYPE.body,
                negative
                  ? 'border-ds-red-line text-ds-red-ink  '
                  : 'border-ds-green-line text-ds-green-ink  ',
                item.current ? 'font-semibold' : 'opacity-90',
              )}
            >
              {at ?? t('patients.hospital_referral.receipt_history_no_time', 'Ukendt tidspunkt')}
              {' · '}
              {receiptKindLabel(item, t)}
              {negative && refuse && <>{' · '}{refuseLabel(refuse, t)}</>}
              {item.current && (
                <>
                  {' · '}
                  {t('patients.hospital_referral.receipt_history_current', 'Gældende')}
                </>
              )}
              {negative && item.refuseText && (
                <span className="block whitespace-pre-wrap font-normal">{item.refuseText}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className={UI_SECTION}>{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className={FORM_LABEL_TYPE}>{label}</div>
      <p className={cn('mt-1 break-words', TYPE.body, 'text-ds-text ')}>{value}</p>
    </div>
  );
}
